import { ChatbotThemeColors, ChatbotThemeGradients } from './constants';
import { HaggleType } from './enums';

import { PlanItem } from '@/interfaces/api/configApp/IPlanItem';
import { HaggleInviteStatus } from '@/interfaces/api/haggle-invite/haggle-invite.enum';
import { HaggleInvite } from '@/interfaces/api/invite/IInvite';
import { INotifications } from '@/interfaces/api/notifications/INotifications';
import { IProductVariant } from '@/interfaces/api/product/IProduct';
import { UserRole } from '@/interfaces/api/user/UserRole.enum';

export const formatDate = (dateString: string): string => {
	const date = new Date(dateString);
	const day = date.getDate();
	const month = date.getMonth() + 1;
	const year = date.getFullYear();

	return `${day}/${month}/${year}`;
};

export const formatToLocalISO = (dateString: string): string => {
	const date = new Date(dateString);

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');

	return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const formatToDateString = (date: Date | null): string | null => {
	if (!date) return '';

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
};

export const formatToTimeString = (date: Date | null): string | null => {
	if (!date) return '';

	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');

	return `${hours}:${minutes}`;
};

export const formatToDateTimeString = (date: Date | null): string | null => {
	if (!date) return '';

	const dateStr = formatToDateString(date);
	const timeStr = formatToTimeString(date);

	return `${dateStr}T${timeStr}`;
};

export const formatToUrlDate = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	return `${year}-${month}-01`;
};

export const formatHaggleEventDate = (
	startDate: string,
	endDate: string,
): string => {
	const startDateObj = new Date(startDate);
	const endDateObj = new Date(endDate);

	// Extract date components from startDate
	const day = startDateObj.getDate();
	const month = startDateObj.getMonth() + 1;
	const year = startDateObj.getFullYear();

	// Format the date part
	const datePart = `${day}/${month}/${year}`;

	// Extract and format start time components
	const startHours = String(startDateObj.getHours()).padStart(2, '0');
	const startMinutes = String(startDateObj.getMinutes()).padStart(2, '0');
	const startTimePart = `${startHours}:${startMinutes}`;

	// Extract and format end time components
	const endHours = String(endDateObj.getHours()).padStart(2, '0');
	const endMinutes = String(endDateObj.getMinutes()).padStart(2, '0');
	const endTimePart = `${endHours}:${endMinutes}`;

	// Combine the time parts into a range
	const timeRangePart = `${startTimePart} - ${endTimePart}`;

	// Combine date and time range parts
	const dateStringFormatted = `${datePart} ${timeRangePart}`;

	return dateStringFormatted;
};

export const formatDateTime = (dateString: string): string => {
	const formatPrueba = formatToLocalISO(dateString);

	const date = new Date(formatPrueba);

	const day = date.getDate();
	const month = date.getMonth() + 1;
	const year = date.getFullYear();

	const hours = date.getHours();
	const minutes = date.getMinutes();
	const timeString =
		minutes >= 10 ? `${hours}:${minutes}hs` : `${hours}:0${minutes}hs`;

	const dateStringFormatted = `${day}/${month}/${year} - ${timeString}`;

	return dateStringFormatted;
};

export const calculateStatus = (endDate: string, startDate: string): string => {
	const actualDate = new Date();
	let status: string;

	if (new Date(startDate) > actualDate) {
		status = 'Pending';
	} else if (
		actualDate < new Date(endDate) &&
		new Date(startDate) < actualDate
	) {
		status = 'Active';
	} else {
		status = 'Finished';
	}

	return status;
};

export const capitalizeAndJoin = (strings: string[]): string => {
	return strings
		.map((str: string) =>
			str.trim() ? str.charAt(0).toUpperCase() + str.slice(1) : '',
		)
		.sort((a, b) => a.localeCompare(b))
		.join(', ');
};

export const isAdminCentral = (roles: string[]): boolean => {
	return roles.includes(UserRole.Admin);
};

export const sanitizeValuesObject = (values: object) => {
	return Object.fromEntries(
		Object.entries(values).map(([key, value]) => [
			key,
			value === '' ? null : value,
		]),
	);
};

export const reduceHoursFromDate = (hours: number, date: Date) => {
	const dateFormat = new Date(date);
	dateFormat.setUTCHours(dateFormat.getUTCHours() - hours);
	return dateFormat.toISOString();
};

export const formatTimestamp = (timestamp: Date) => {
	const dateObj = new Date(timestamp);
	const date = dateObj.toISOString().split('T')[0];
	const time = dateObj.toISOString().split('T')[1].split('.')[0];
	return { date, time };
};

export const checkDateTimeValid = (dateTime: string) => {
	const now = new Date();
	const inputDate = new Date(dateTime);
	return inputDate < now;
};

export const formattedMonth = (date: string) => {
	return date
		? new Date(date).toLocaleString('default', {
				month: 'long',
				year: 'numeric',
		  })
		: '';
};

export const getMatchedVariant = (
	variants: IProductVariant[],
	options: Record<string, string>,
) => {
	return variants.find((variant) =>
		Object.entries(options).every(
			([key, val]) =>
				(variant as unknown as Record<string, string>)[key] === val,
		),
	);
};

export const getImageNameFromUrl = (url: string): string => {
	const parts = url.split('/');
	return parts.length > 0 ? parts[parts.length - 1] : '';
};

export const getThemeValue = (theme: string): string | undefined => {
	const colorMatch = ChatbotThemeColors.find((item) => item.name === theme);
	if (colorMatch) return colorMatch.value;

	const gradientMatch = ChatbotThemeGradients.find(
		(item) => item.name === theme,
	);
	if (gradientMatch) return gradientMatch.value;

	return theme;
};

export const checkUserRoleRubyOrSapphireOrDiamond = (roles: string[]) => {
	return roles.some((role) =>
		[
			UserRole.MerchantDiamond,
			UserRole.MerchantRuby,
			UserRole.MerchantSapphire,
		].includes(role as UserRole),
	);
};

// New utility function to convert a time string or date to a Date object
export const parseTimeValue = (value: string | Date | null): Date | null => {
	if (!value) return null;

	if (typeof value === 'string') {
		if (value.includes(':') && !value.includes('T')) {
			const today = new Date();
			const [hours, minutes] = value.split(':').map(Number);
			today.setHours(hours, minutes, 0, 0);
			return today;
		}
	}

	// If it's already a Date or a full date string
	return new Date(value);
};

// Helper function to check if a string is a valid YYYY-MM-DD date
export const isValidDateString = (dateString: string | undefined): boolean => {
	if (!dateString) return false;
	const regex = /^\d{4}-\d{2}-\d{2}$/;
	if (!regex.test(dateString)) return false;
	// Check if the date is valid by parsing it (treat as local time start of day)
	const date = new Date(dateString + 'T00:00:00');
	return !isNaN(date.getTime());
};

export const extractDiscountCodeInfoFromUrl = (url: string) => {
	try {
		const parsedUrl = new URL(url);
		const pathSegments = parsedUrl.pathname.split('/');
		const cartInfo = pathSegments.find((segment) => segment.includes(':'));
		if (!cartInfo) return null;

		const [variantId, quantity] = cartInfo.split(':');
		const discountCode = parsedUrl.searchParams.get('discount');

		if (!variantId || !quantity || !discountCode) return null;

		return {
			variantId,
			quantity,
			discountCode,
		};
	} catch {
		return null;
	}
};

export function isFavouriteProduct(
	product: any,
	favouriteMerchants: INotifications[] = [],
): boolean {
	if (!('haggle' in product) || !favouriteMerchants) return false;

	const productUserId = product?.haggle?.user?.username;
	if (!productUserId) return false;

	return favouriteMerchants.some(
		(fav) => fav?.favouritedUserId === productUserId,
	);
}

export const mapConfigPlans = (data: any[]): PlanItem[] => {
	if (!Array.isArray(data)) return [];

	return data
		.map((item) => {
			const plan = item.value;
			if (!plan || typeof plan !== 'object') return null;
			return {
				name: plan.name,
				price: plan.price,
				priceNote: plan.priceNote,
				background: plan.background,
				badgeColor: plan.badgeColor,
				shadowColor: plan.shadowColor,
				features: plan.features,
				role: plan.role,
				planName: plan.planName,
				limit: plan.limit,
				pricePerExtraHaggle: plan.pricePerExtraHaggle,
				id: plan.id ?? item.id, // fallback to item.id if plan.id is missing
			};
		})
		.filter(Boolean) as PlanItem[];
};

export function getHaggleApprovalStatus(
	product: any,
	invitedUsers: HaggleInvite[] = [],
): {
	approved: boolean;
	status: HaggleInviteStatus;
} {
	if (!('haggle' in product) || !product.haggle?.id || !invitedUsers?.length) {
		return { approved: false, status: HaggleInviteStatus.REJECTED };
	}

	const haggleEventId = product.haggle.id;

	const invite = invitedUsers.find(
		(inv) => inv.haggleEventId === haggleEventId,
	);

	if (!invite) {
		return { approved: false, status: HaggleInviteStatus.REJECTED };
	}

	switch (invite.status?.toLowerCase()) {
		case 'approved':
			return { approved: true, status: HaggleInviteStatus.APPROVED };
		case 'pending':
			return { approved: false, status: HaggleInviteStatus.PENDING };
		case 'rejected':
			return { approved: false, status: HaggleInviteStatus.REJECTED };
		default:
			return { approved: false, status: HaggleInviteStatus.REJECTED };
	}
}

export const getHaggleTypeDisplayText = (haggleType: any): string => {
	let displayType = 'B2C';

	if (haggleType) {
		if (haggleType === HaggleType.PUBLIC) {
			displayType = 'B2B - Public';
		} else if (haggleType === HaggleType.SEMI_PRIVATE) {
			displayType = 'B2B - Semi Private';
		} else if (haggleType === HaggleType.PRIVATE) {
			displayType = 'B2B - Private';
		}
	}
	return displayType;
};

export const formatPrice = (price: number | string): string => {
	const numPrice = typeof price === 'string' ? parseFloat(price) : price;
	if (isNaN(numPrice)) return '$0';

	return (
		'$' +
		numPrice.toLocaleString('en-US', {
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
		})
	);
};

export const formatDateTimeShort = (dateString: string | Date): string => {
	const startDate = new Date(dateString);
	const monthNames = [
		'Jan',
		'Feb',
		'Mar',
		'Apr',
		'May',
		'Jun',
		'Jul',
		'Aug',
		'Sep',
		'Oct',
		'Nov',
		'Dec',
	];
	const month = monthNames[startDate.getMonth()];
	const day = startDate.getDate();

	let hours = startDate.getHours();
	const minutes = startDate.getMinutes();
	const ampm = hours >= 12 ? 'pm' : 'am';
	hours = hours % 12;
	hours = hours ? hours : 12; // the hour '0' should be '12'
	const minutesStr = minutes < 10 ? '0' + minutes : minutes.toString();

	return `${month} ${day} @${hours}:${minutesStr}${ampm}`;
};
